import React, { useEffect, useState } from 'react';
import {
  TextField,
  Button,
  Box,
  CircularProgress,
  Typography,
  IconButton,
  Grid,
  Paper,
  Divider,
  Alert,
  useTheme,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import UpdateIcon from '@mui/icons-material/Update';
import ModalMAIQ from 'app/components/ModalMAIQ';
import { useRole } from "app/context/RoleContext";
import { useScope } from "app/context/ScopeContext";
import DualListMAIQ from 'app/components/DualListMAIQ';
import ConfirmationModal from 'app/components/ConfirmationModal';
import CopyToClipboard from 'app/components/CopyToClipboard';
import CellChipStatus from 'app/components/ReusableUIComponents/HighLevelComponents/DataGridMAIQ/components/cells/CellChipStatus';
import ScopeDetailsModal from '../scope/ScopeDetailsModal';
import { formatToLocalTime } from 'utils/dateUtils';
import { Role, Scope } from '../../types';

interface RoleDetailsModalProps {
  open: boolean;
  onClose: () => void;
  data: Role;
  setRefreshKey: React.Dispatch<React.SetStateAction<number>>;
}

const RoleDetailsModal: React.FC<RoleDetailsModalProps> = ({ open, onClose, data, setRefreshKey }) => {
  const theme = useTheme();
  const { editRole, deleteRole } = useRole();
  const { scopes, getAllScopes } = useScope();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
  const [error, setError] = useState<string>("");
  const [selectedScope, setSelectedScope] = useState<Scope | null>(null);

  const [role, setRole] = useState<Role>(data);
  const [originalData, setOriginalData] = useState<Role>(data);
  const [roleScopes, setRoleScopes] = useState<any[]>([]);

  // Get all scopes when modal opens
  useEffect(() => {
    if (open) {
      getAllScopes();
    }
  }, [open]);

  // Update role when data changes
  useEffect(() => {
    setRole(data);
    setOriginalData(data);
  }, [data]);

  // Find role scopes when scopes data is loaded
  useEffect(() => {
    if (scopes.length > 0 && role._id) {
      // Find scopes that belong to this role
      const roleScopeObjects = scopes.filter(scope => 
        role.scopes && role.scopes.some(roleScope => roleScope._id === scope._id)
      );
      setRoleScopes(roleScopeObjects);
    }
  }, [scopes, role._id, role.scopes]);

  // Convert scope objects to DualListMAIQ format
  const convertedScopes = (scopeObjects: any[]) => {
    return scopeObjects.map(scope => ({
      id: scope._id,
      label: scope.name,
    }));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setRole(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleScopeUpdate = (updatedScopes: any[]) => {
    // Extract scope IDs from the updatedScopes and update the role
    const scopeIds = updatedScopes.map(scope => scope.id);
    setRole(prev => ({ ...prev, scopes: scopeIds }));
  };

  const handleEditSubmit = async () => {
    if (!role._id) return;
    setIsSubmitting(true);
    try {
      await editRole(role._id, {
        name: role.name,
        scopes: role.scopes,
        group_can_be_attached: role.group_can_be_attached,
        list_can_be_attached: role.list_can_be_attached,
        access_to_all_dealership: role.access_to_all_dealership,
      });
      
      // Update the refreshKey to trigger a re-render in parent component
      setRefreshKey(prev => prev + 1);
      
      // Close the modal
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update role');
    } finally {
      setIsSubmitting(false);
      setOpenConfirm(false);
    }
  };

  const handleDeleteRole = async () => {
    if (!role._id) return;
    setIsDeleting(true);
    try {
      await deleteRole(role._id);
      
      // Update the refreshKey to trigger a re-render in parent component
      setRefreshKey(prev => prev + 1);
      
      // Close the modal
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete role');
    } finally {
      setIsSubmitting(false);
      setOpenDeleteConfirm(false);
    }
  };

  const handleCancelEdit = () => {
    setRole(originalData);
    setIsEditing(false);
  };

  const handleClose = () => {
    setIsEditing(false);
    setError("");
    onClose();
  };

  const handleScopeClick = (scope: Scope) => {
    setSelectedScope(scope);
  };

  const handleCloseScopeDetails = () => {
    setSelectedScope(null);
  };

  return (
    <>
      <ModalMAIQ 
        open={open} 
        onClose={handleClose} 
        hideDefaultCloseButton={true}
      >
        <Paper
          elevation={0}
          sx={{
            width: '800px',
            maxWidth: '90vw',
            maxHeight: '85vh',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 0,
          }}
        >
          {/* Header - fixed */}
          <Box
            sx={{
              p: 2,
              backgroundColor: '#0F2A52',
              color: 'white',
              zIndex: 10,
              flexShrink: 0,
            }}
          >
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              width="100%"
            >
              <Typography variant="h6" fontWeight="600">
                Role Details
              </Typography>
              <Box>
                <Tooltip title="Edit Role">
                  <IconButton
                    onClick={() => {
                      setIsEditing(true);
                      setOriginalData(role);
                    }}
                    sx={{ color: 'white', p: 0.5 }}
                    size="small"
                    disabled={isEditing}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete Role">
                  <IconButton 
                    onClick={() => setOpenDeleteConfirm(true)} 
                    sx={{ color: 'white', p: 0.5, ml: 1 }}
                    size="small"
                  >
                    {isDeleting ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>
                <Tooltip title="Close">
                  <IconButton
                    aria-label="close"
                    onClick={handleClose}
                    sx={{
                      color: 'white',
                      p: 0.5,
                      ml: 1
                    }}
                    size="small"
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
            <Typography variant="subtitle2" sx={{ mt: 0.5, fontWeight: 400, fontSize: '0.8rem' }}>
              ID: {role._id}
              {role._id && <CopyToClipboard text={role._id} size="small" />}
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mx: 2, mt: 2 }}>
              {error}
            </Alert>
          )}

          {/* Scrollable content area */}
          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              position: 'relative',
              p: 3,
            }}
          >
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                    Role Name *
                  </Typography>
                  <TextField
                    fullWidth
                    name="name"
                    value={role.name}
                    onChange={handleChange}
                    variant="outlined"
                    placeholder="Enter role name"
                    disabled={!isEditing}
                    size="small"
                    inputProps={{
                      sx: { py: 1.5 }
                    }}
                  />
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Scopes
                </Typography>
                
                {isEditing ? (
                  <Box sx={{ mt: 1, width: '100%' }}>
                    {scopes.length > 0 && (
                      <DualListMAIQ
                        key={`dual-list-${scopes.length}-${role._id}`}
                        availableItems={convertedScopes(
                          scopes.filter(scope => !role.scopes?.some(roleScope => roleScope._id === scope._id))
                        )}
                        selectedItems={convertedScopes(roleScopes)}
                        onUpdate={handleScopeUpdate}
                        listHeight={250}
                      />
                    )}
                  </Box>
                ) : (
                  <TableContainer 
                    component={Paper}
                    sx={{ 
                      maxHeight: '300px',
                      '&::-webkit-scrollbar': {
                        width: '8px',
                      },
                      '&::-webkit-scrollbar-thumb': {
                        backgroundColor: 'rgba(0,0,0,0.2)',
                        borderRadius: '4px',
                      }
                    }}
                  >
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Name</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Scope</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {roleScopes.length > 0 ? (
                          roleScopes.map((scope, index) => (
                            <TableRow 
                              key={scope._id}
                              onClick={() => handleScopeClick(scope)}
                              sx={{ 
                                cursor: 'pointer',
                                '&:hover': {
                                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                },
                                transition: 'background-color 0.2s ease',
                              }}
                            >
                              <TableCell>{scope.name}</TableCell>
                              <TableCell>
                                <Typography 
                                  variant="body2" 
                                  sx={{ 
                                    fontFamily: 'monospace',
                                    bgcolor: 'rgba(0,0,0,0.05)',
                                    p: 0.5,
                                    borderRadius: 0.5,
                                    display: 'inline-block'
                                  }}
                                >
                                  {scope.scope}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <CellChipStatus>
                                  {scope.status}
                                </CellChipStatus>
                              </TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={3} align="center" sx={{ fontStyle: 'italic', color: 'text.primary' }}>
                              No scopes assigned to this role
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Role Options
                </Typography>
                {isEditing ? (
                  <Box>
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="group_can_be_attached"
                          checked={!!role.group_can_be_attached}
                          onChange={handleChange}
                          disabled={isSubmitting}
                        />
                      }
                      label="Group can be attached"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="list_can_be_attached"
                          checked={!!role.list_can_be_attached}
                          onChange={handleChange}
                          disabled={isSubmitting}
                        />
                      }
                      label="List can be attached"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="access_to_all_dealership"
                          checked={!!role.access_to_all_dealership}
                          onChange={handleChange}
                          disabled={isSubmitting}
                        />
                      }
                      label="Access to all dealership"
                    />
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mt: 1 }}>
                    <Typography variant="body2">
                      <b>Group can be attached:</b> {role.group_can_be_attached ? 'Yes' : 'No'}
                    </Typography>
                    <Typography variant="body2">
                      <b>List can be attached:</b> {role.list_can_be_attached ? 'Yes' : 'No'}
                    </Typography>
                    <Typography variant="body2">
                      <b>Access to all dealership:</b> {role.access_to_all_dealership ? 'Yes' : 'No'}
                    </Typography>
                  </Box>
                )}
              </Grid>

              <Grid item xs={12}>
                <Paper 
                  variant="outlined" 
                  sx={{ 
                    mt: 2, 
                    p: 2, 
                    borderRadius: 1, 
                    bgcolor: 'rgba(0, 0, 0, 0.02)',
                    border: '1px solid rgba(0, 0, 0, 0.1)'
                  }}
                >
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Metadata
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Tooltip title="Creation Date">
                          <AccessTimeIcon fontSize="small" sx={{ color: '#666', fontSize: '1rem' }} />
                        </Tooltip>
                        <Typography variant="body2" sx={{ color: '#666', fontSize: '0.9rem' }}>
                          Created: {role.created_at ? formatToLocalTime(role.created_at) : 'N/A'}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Tooltip title="Last Update Date">
                          <UpdateIcon fontSize="small" sx={{ color: '#666', fontSize: '1rem' }} />
                        </Tooltip>
                        <Typography variant="body2" sx={{ color: '#666', fontSize: '0.9rem' }}>
                          Last Updated: {role.updated_at ? formatToLocalTime(role.updated_at) : 'N/A'}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            </Grid>
          </Box>

          <Divider />
          
          {/* Footer with action buttons - fixed at bottom */}
          <Box 
            sx={{ 
              p: 2, 
              display: 'flex', 
              justifyContent: 'flex-end',
              backgroundColor: 'white',
              borderTop: '1px solid rgba(0, 0, 0, 0.12)',
              zIndex: 9,
              flexShrink: 0,
            }}
          >
            {isEditing && (
              <Tooltip title="Save your changes">
                <Button
                  variant="contained"
                  color="primary"
                  disabled={isSubmitting}
                  onClick={() => setOpenConfirm(true)}
                  startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                  sx={{ 
                    textTransform: 'none', 
                    fontWeight: 'bold', 
                    bgcolor: '#0F2A52',
                    color: 'white',
                    py: 0.75,
                    px: 3
                  }}
                >
                  {isSubmitting ? 'Saving...' : 'Save Changes'}
                </Button>
              </Tooltip>
            )}
          </Box>

          {/* Confirmation Modals */}
          <ConfirmationModal
            open={openConfirm}
            onClose={() => setOpenConfirm(false)}
            onConfirm={handleEditSubmit}
            message="Are you sure you want to save the changes?"
          />
          <ConfirmationModal
            open={openDeleteConfirm}
            onClose={() => setOpenDeleteConfirm(false)}
            onConfirm={handleDeleteRole}
            message="Are you sure you want to delete this role?"
          />
        </Paper>
      </ModalMAIQ>

      {selectedScope && (
        <ScopeDetailsModal
          open={!!selectedScope}
          onClose={handleCloseScopeDetails}
          scope={selectedScope}
          onSuccess={() => {
            setRefreshKey(prev => prev + 1);
            handleCloseScopeDetails();
          }}
        />
      )}
    </>
  );
};

export default RoleDetailsModal; 